import React from 'react';
import { Wallet, UserCheck, Bell, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Dialog, KeyValueList, Money } from '@/components/ui';

interface ManualDepositWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  amount: number;
  providerName: string;
  isLoading?: boolean;
}

const STEPS = [
  { icon: Wallet, text: 'Funds reserved from wallet immediately' },
  { icon: UserCheck, text: 'Staff deposits manually into game' },
  { icon: Bell, text: 'Notification sent when confirmed' },
  { icon: RotateCcw, text: 'Auto-refund after 2 hours if needed' },
] as const;

const ManualDepositWarningModal: React.FC<ManualDepositWarningModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  amount,
  providerName,
  isLoading = false,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      size="sm"
      placement="center"
      dismissible={!isLoading}
      closeLabel={t('common.close')}
      title="Manual Deposit Notice"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onConfirm} loading={isLoading}>
            {isLoading ? 'Processing...' : 'Confirm Deposit'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Alert tone="warning">
          This game requires manual processing by our staff. Your deposit will be processed within{' '}
          <strong className="font-semibold text-primary">5 minutes to 2 hours</strong>.
        </Alert>

        <ul className="m-0 flex list-none flex-col gap-3 rounded-control border border-edge-subtle bg-inset p-3">
          {STEPS.map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-start gap-3 font-ui text-small text-primary">
              <Icon className="mt-0.5 size-4 shrink-0 text-link" aria-hidden />
              {text}
            </li>
          ))}
        </ul>

        <KeyValueList
          items={[
            { label: 'Amount', value: <Money amount={amount} direction="in" />, emphasis: true },
            { label: 'Game', value: providerName },
          ]}
        />
      </div>
    </Dialog>
  );
};

export default ManualDepositWarningModal;
